import { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const subscribers = useRef(new Set());
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const subscribe = useCallback((fn) => {
    subscribers.current.add(fn);
    return () => subscribers.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!user) return;

    let closed = false;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws`;

    const connect = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          let msg = {};
          try { msg = JSON.parse(e.data); } catch { /* ignore */ }
          subscribers.current.forEach((fn) => { try { fn(msg); } catch { /* */ } });
        };
        ws.onclose = () => {
          if (!closed) reconnectRef.current = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* */ } };
      } catch {
        reconnectRef.current = setTimeout(connect, 3000);
      }
    };
    const startTimer = setTimeout(connect, 500);

    return () => {
      closed = true;
      clearTimeout(startTimer);
      clearTimeout(reconnectRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* */ } }
    };
  }, [user]);

  return <RealtimeContext.Provider value={{ subscribe }}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(callback) {
  const ctx = useContext(RealtimeContext);
  const cbRef = useRef(callback);
  cbRef.current = callback;
  useEffect(() => {
    if (!ctx) return;
    return ctx.subscribe((msg) => cbRef.current(msg));
  }, [ctx]);
}
